import { useGetAllWorkflowExecutionsQuery, useCancelWorkflowInstanceMutation, useRetryWorkflowInstanceMutation, usePauseWorkflowInstanceMutation, useResumeWorkflowInstanceMutation } from "@/services/WorkFlowBuilderApi";
import { DataTable } from "@/components/ui/table/DataTable";
import { ColumnDef } from "@tanstack/react-table";
import { WorkflowResponse } from "@/app/types/workflow";
import { Button } from "@/components/ui/Button";
import { useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import { Pause, Play, X } from "lucide-react";
import { Card } from "@/components/ui/Card";
import { DataTableColumnHeader } from "@/components/ui/table/DataTableColumnHeader";
import useTableData from "@/components/ui/table/DataTableLogic";
import TableSearchFilter from "@/components/ui/table/DataTableSearch";
import Pagination from "@/components/ui/table/PaginationTable";
import usePagination from "@/hooks/UsePagination";
import TableFilters from "@/components/ui/table/DataTableFilter";
import { useState, useMemo } from "react";
import { Badge } from "@/components/ui/Badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/MobileToolTip";
import useDocumentTitle from "@/hooks/UseDocumentTitle";
import { useToast } from "@/components/ui/UseToast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/AlertDialog";
import { ReloadIcon } from "@radix-ui/react-icons";
import { displayValue } from "@/utils/Helper";

const initialValue = {
  limit: 10,
  offset: 0,
  sort_order: "desc",
  sort_key: "id",
};

export const WorkflowExecutionList = () => {
  useDocumentTitle("Workflow Executions");
  const [inputValue, setInputValue] = useState<string>("");
  const [openFilter, setOpenFilter] = useState(false);
  const [filterData, setFilterData] = useState({});
  const { toast } = useToast();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [workflowToDelete, setWorkflowToDelete] = useState<{ id: number; name: string } | null>(null);
  const [cancelWorkflowInstance] = useCancelWorkflowInstanceMutation();
  const [retryWorkflowInstance] = useRetryWorkflowInstanceMutation();
  const [pauseWorkflowInstance] = usePauseWorkflowInstanceMutation();
  const [resumeWorkflowInstance] = useResumeWorkflowInstanceMutation();

  const [searchParams] = useSearchParams();
  const workflow_id = searchParams.get("id");

  const { searchCriteria, toggleSortOrder, setSearchCriteria } =
    useTableData(initialValue);

  const { data: executionsResponse, isLoading } = useGetAllWorkflowExecutionsQuery({
    id: workflow_id!,
    offset: searchCriteria.offset,
    limit: searchCriteria.limit,
    search_key: inputValue || undefined,
    sort_key: searchCriteria.sort_key,
    sort_order: searchCriteria.sort_order,
    status: (filterData as any).status,
  });

  // Extract workflows array and total count from response
  const workflows = executionsResponse && typeof executionsResponse === 'object' && 'data' in executionsResponse
    ? executionsResponse.data
    : Array.isArray(executionsResponse)
      ? executionsResponse
      : [];

  const totalRecords = executionsResponse && typeof executionsResponse === 'object' && 'filtered_count' in executionsResponse
    ? executionsResponse.filtered_count
    : Array.isArray(workflows)
      ? workflows.length
      : 0;

  const tableData = useMemo(() => {
    return Array.isArray(workflows) ? workflows : [];
  }, [workflows]);

  const confirmDelete = async () => {
    if (!workflowToDelete) return;

    try {
      // await deleteWorkflow(workflowToDelete.id).unwrap();
      // toast({
      //   variant: "success",
      //   title: "Success",
      //   description: "Workflow deleted successfully!",
      // });
    } catch (error) {
      console.error("Failed to delete workflow:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to delete workflow. Please try again.",
      });
    } finally {
      setDeleteDialogOpen(false);
      setWorkflowToDelete(null);
    }
  };

  const columns: ColumnDef<WorkflowResponse>[] = [
    {
      accessorKey: "id",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="ID"
          sortKey="id"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center capitalize gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
            {
              <button
                onClick={() => { window.location.href = `/admin/workflow-simulation?id=${row.original.id}&workflow_id=${row.original.workflow_id}&workflow_version_id=${row.original.workflow_version_id}` }}
                className="underline"
              >
                {displayValue(row.original.id)}
              </button>
            }
          </div>
        );
      },
    },
    {
      accessorKey: "workflow_id",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Workflow ID"
          sortKey="workflow_id"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <Tooltip>
            <TooltipTrigger>
              <p className="capitalize text-nowrap max-w-[200px] truncate overflow-hidden">
                {row.original.workflow_id}
              </p>
            </TooltipTrigger>
            <TooltipContent>{row.original.workflow_id}</TooltipContent>
          </Tooltip>
        );
      },
    },
    {
      accessorKey: "workflow_version_id",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Workflow Version ID"
          sortKey="workflow_version_id"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <Tooltip>
            <TooltipTrigger>
              <p className="capitalize text-nowrap max-w-[200px] truncate overflow-hidden">
                {row.original.workflow_version_id}
              </p>
            </TooltipTrigger>
            <TooltipContent>{row.original.workflow_version_id}</TooltipContent>
          </Tooltip>
        );
      },
    },
    {
      accessorKey: "started_at",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Started At"
          sortKey="started_at"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        const date = row.getValue("started_at") as string;
        return date ? format(new Date(date), "yyyy-MM-dd HH:mm") : "-";
      },
    },
    {
      accessorKey: "status",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Status"
          sortKey="status"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        const status = row.original.status || "pending";
        return (
          <Badge
            className={`${status === "completed"
              ? "bg-green-600"
              : status === "failed"
                ? "bg-red-500"
                : "bg-gray-500"
              } text-white font-medium`}
          >
            {status}
          </Badge>
        );
      },
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        return (
          <div className="flex gap-2">
            {!["completed", "cancelled"].includes(row.original.status) && <Button
              variant="ghost"
              size="icon"
              onClick={() => cancelWorkflowInstance(row.original.id).unwrap()}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
              title="Cancel"
            >
              <X className="w-4 h-4" />
            </Button>}
            {["running"].includes(row.original.status) && <Button
              variant="ghost"
              size="icon"
              onClick={() => pauseWorkflowInstance(row.original.id).unwrap()}
              className="text-yellow-600 hover:text-yellow-700 hover:bg-yellow-50"
              title="Pause"
            >
              <Pause className="w-4 h-4" />
            </Button>}
            {["paused"].includes(row.original.status) && <Button
              variant="ghost"
              size="icon"
              onClick={() => resumeWorkflowInstance(row.original.id).unwrap()}
              className="text-green-600 hover:text-green-700 hover:bg-green-50"
              title="Resume"
            >
              <Play className="w-4 h-4" />
            </Button>}
            {row.original.status === "failed" && <Button
              variant="ghost"
              size="icon"
              onClick={() => retryWorkflowInstance(row.original.id).unwrap()}
              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
              title="Retry"
            >
              <ReloadIcon className="w-4 h-4" />
            </Button>}
          </div>
        );
      },
    },
  ];

  const {
    handleLimitChange,
    handleNextPage,
    handlePrevPage,
    handleFirstPage,
    handleLastPage,
    isLastPage,
  } = usePagination({
    totalRecords,
    searchCriteria,
    setSearchCriteria,
  });

  const handleFilterChange = (filterName: string, value: string) => {
    setFilterData((prevFilters) => {
      const newFilters = {
        ...prevFilters,
        [filterName]: value,
      };
      return newFilters;
    });
  };

  const filterDisplay = [
    {
      type: "select",
      name: "status",
      label: "Status",
      options: [
        { value: "pending", label: "Pending" },
        { value: "running", label: "Running" },
        { value: "completed", label: "Completed" },
        { value: "failed", label: "Failed" },
        { value: "paused", label: "Paused" },
        { value: "cancelled", label: "Cancelled" },
      ],
      function: (value: string) => handleFilterChange("status", value),
    },
  ];

  return (
    <div className="w-full p-5">
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Workflow</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete workflow "{workflowToDelete?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-row justify-start gap-2">
            <AlertDialogCancel className="w-24 m-0">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="w-24 bg-red-600 hover:bg-red-700 m-0"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Card className="pt-4">
        <div className="flex flex-row lg:items-center justify-between gap-4 px-3">
          <TableSearchFilter
            inputValue={inputValue}
            setInputValue={setInputValue}
            setSearchCriteria={setSearchCriteria}
            placeHolder={"Search executions"}
          />

          <div className="flex flex-row lg:flex-row lg:justify-center lg:items-center gap-2">
            <button
              className={`border rounded-full size-3 text-gray-400 p-4 flex items-center justify-center ${Object.keys(filterData).length ? "border-primary" : ""}`}
              onClick={() => setOpenFilter(true)}
            >
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <i
                      className={`fa fa-filter ${Object.keys(filterData).length ? "text-primary" : ""}`}
                    ></i>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">
                    <p className="capitalize">click to apply filter</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </button>
          </div>
        </div>

        <div className="mt-4 bg-white">
          <DataTable
            columns={columns}
            data={tableData}
            isLoading={isLoading}
            searchMessage="No executions found"
          />
        </div>

        {tableData.length > 0 && (
          <Pagination
            limit={searchCriteria.limit}
            offset={searchCriteria.offset}
            totalItems={totalRecords}
            onLimitChange={handleLimitChange}
            onNextPage={handleNextPage}
            onPrevPage={handlePrevPage}
            onFirstPage={handleFirstPage}
            onLastPage={handleLastPage}
            isLastPage={isLastPage}
          />
        )}

        <TableFilters
          isOpen={openFilter}
          setOpen={setOpenFilter}
          initialValue={initialValue}
          filterData={filterData}
          setFilter={setFilterData}
          setSearchCriteria={setSearchCriteria}
          filterDisplay={filterDisplay}
          setInputValue={setInputValue}
        />
      </Card>
    </div>
  );
};
